import React from 'react';
import { Flame, Skull, Zap } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = ({ heroImage, productImage }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Fire background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black"></div>
      </div>

      {/* Animated Fire Particles */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-600 rounded-full animate-ping opacity-75"></div>
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-orange-500 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-yellow-500 rounded-full animate-ping opacity-70" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-red-950/50 border-2 border-red-600 px-6 py-2 rounded-full mb-6 animate-pulse">
              <Skull className="w-5 h-5 text-red-500" />
              <span className="text-red-500 font-black tracking-widest text-sm">EXTREMAMENTE PICANTE</span>
              <Skull className="w-5 h-5 text-red-500" />
            </div>

            {/* Main Title */}
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-4 tracking-tight">
              RASPUTIN
            </h1>
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-8">
              <div className="h-1 w-16 bg-red-600"></div>
              <p className="text-2xl sm:text-3xl text-yellow-500 font-bold tracking-widest">
                MOLHO PICANTE
              </p>
              <div className="h-1 w-16 bg-red-600"></div>
            </div>

            {/* Description */}
            <p className="text-xl text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              O molho mais <span className="text-red-500 font-bold">intenso</span> que você já experimentou. 
              Prepare-se para sentir o <span className="text-orange-500 font-bold">fogo</span> do inferno.
            </p>

            {/* Features Icons */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-10">
              <div className="flex items-center gap-2">
                <Flame className="w-6 h-6 text-red-600" />
                <span className="text-white font-bold">Extremo Calor</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-500" />
                <span className="text-white font-bold">Sabor Intenso</span>
              </div>
              <div className="flex items-center gap-2">
                <Skull className="w-6 h-6 text-red-500" />
                <span className="text-white font-bold">Sem Limites</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={() => scrollToSection('intensity')}
                className="bg-red-600 hover:bg-red-700 text-white font-black text-lg px-8 py-6 shadow-2xl shadow-red-600/50 transition-all duration-300 hover:shadow-red-600/80 hover:scale-105"
              >
                <Flame className="w-5 h-5 mr-2" />
                TESTE SUA CORAGEM
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                variant="outline"
                className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-black text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
              >
                ONDE COMPRAR
              </Button>
            </div>
          </div>

          {/* Product Image */}
          <div className="relative flex items-center justify-center">
            <div className="relative z-10">
              <img
                src={productImage}
                alt="Rasputin Hot Sauce"
                className="w-full max-w-md mx-auto drop-shadow-2xl animate-float"
              />
              {/* Glow Effect */}
              <div className="absolute inset-0 blur-3xl bg-gradient-to-t from-red-600/30 via-orange-600/20 to-transparent -z-10"></div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="flex flex-col items-center gap-2 text-white/70">
          <span className="text-sm font-bold tracking-wider">DESCUBRA MAIS</span>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-scroll"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
